// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { AddCartLinesApiParameter, addLineCollection } from "@insite/client-framework/Services/CartService";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { getOpenBidDetails } from "@steiner/App/Services/OpenBidsService";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";

interface Props {
    result: {
        errorMessage?: string;
        order?: any;
    };
    productIds?: string[];
    listOfLineItemsInfoMap?: any;
    cartLines?: CartLineModel[];
}

export const ACCEPT_BID_PRODUCTS_ID = "acceptBids";

export interface AcceptBidParameter {
    generationId?: string;
    orderNumber?: string;
}

type HandlerType = Handler<AcceptBidParameter & HasOnSuccess & HasOnError<string>, Props>;

export const DispatchBeginAcceptingBid: HandlerType = props => {
    props.dispatch({
        type: "xOpenBids/BeginLoadingOpenBids",
        isLoading: true,
    } as any);
};

export const RequestDataFromApi: HandlerType = async props => {
    props.result = { errorMessage: "", order: {} };
    try {
        props.result.order = await getOpenBidDetails({
            generationId: props.parameter.generationId,
            orderNumber: props.parameter.orderNumber,
        });
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        throw error;
    }
};

export const DispatchGetProducts: HandlerType = async props => {
    props.productIds = props.result.order.salesOrder.lineItemList.lineItem.map(
        (item: any) => item.partIdentifiers.productId,
    );
    await loadProductInfoList({
        id: ACCEPT_BID_PRODUCTS_ID,
        forceLoad: true,
        getProductCollectionParameter: {
            productIds: props.productIds,
        },
    });
};

export const PrepareLineItemsInfoMap: HandlerType = props => {
    const lineItems = props.result.order.salesOrder.lineItemList.lineItem;
    props.listOfLineItemsInfoMap = lineItems.reduce((acc: any, p: any) => {
        const productId = p.partIdentifiers.productId;
        acc[productId] = {
            productNumber: p.partIdentifiers.eclipsePartNumber,
            productDescription: p.partIdentifiers.description,
            qtyOrdered: p.qtyOrdered.quantity.text,
            unitOfMeasure: p.qtyOrdered.quantity.uom,
            bidUnitPrice: p.lineItemPrice.unitPrice,
            bidListPrice: p.lineItemPrice.listPrice,
            bidExtendedPrice: p.lineItemPrice.extendedPrice,
            properties: p.properties,
        };
        return acc;
    }, {});
};

export const PrepareCartLines: HandlerType = props => {
    props.cartLines = [];
    props.productIds?.forEach((productId: string) => {
        const productState = getProductState(props.getState(), productId);
        const product = productState.value;
        const lineItemInfo = props.listOfLineItemsInfoMap[`${productId}`];
        const lineItemInfoProps = lineItemInfo.properties as { [key: string]: string };
        if (product && product.canAddToCart) {
            const cartLine = {} as any;
            cartLine.productId = product.id;
            cartLine.qtyOrdered = lineItemInfo.qtyOrdered;
            cartLine.unitOfMeasure = lineItemInfo.unitOfMeasure;
            cartLine.properties = {
                isBidItem: "true",
                bidUnitPrice: lineItemInfo.bidUnitPrice,
                bidListPrice: lineItemInfo.bidListPrice,
                bidExtendedPrice: lineItemInfo.bidExtendedPrice,
                bidNumber: props.result.order.salesOrder.orderHeader.orderId,
                bidGenerationId: props.result.order.salesOrder.orderHeader.generationId,
                bidCustomer: props.result.order.salesOrder.orderHeader.billTo.entity.entityName,
                bidDateSubmitted: props.result.order.salesOrder.orderHeader.orderDate.date,
                bidCustomerNotes: props.result.order.salesOrder.orderHeader.internalNotes,
                isWidgetNonStockItem: lineItemInfoProps["isWidgetNonStockItem"]
                    ? lineItemInfoProps["isWidgetNonStockItem"]
                    : "",
                nonStockDescription: lineItemInfoProps["nonStockDescription"]
                    ? lineItemInfoProps["nonStockDescription"]
                    : "",
                nonStockPartNo: lineItemInfoProps["nonStockPartNo"] ? lineItemInfoProps["nonStockPartNo"] : "",
            };

            props.cartLines && props.cartLines.push(cartLine);
        } else {
            const cartLine = {} as any;
            cartLine.productId = getSettingsCollection(props.getState()).customSharedSettings.nonStockProductId;
            cartLine.qtyOrdered = lineItemInfo.qtyOrdered;
            cartLine.unitOfMeasure = lineItemInfo.unitOfMeasure;

            cartLine.properties = {
                isBidItem: "true",
                bidUnitPrice: lineItemInfo.bidUnitPrice,
                bidListPrice: lineItemInfo.bidListPrice,
                bidExtendedPrice: lineItemInfo.bidExtendedPrice,
                NonStockPartNo: lineItemInfo.productNumber,
                NonStockDescription: lineItemInfo.productDescription,
                IsWidgetNonStockItem: "true",
                bidNumber: props.result.order.salesOrder.orderHeader.orderId,
                bidGenerationId: props.result.order.salesOrder.orderHeader.generationId,
                bidCustomer: props.result.order.salesOrder.orderHeader.billTo.entity.entityName,
                bidDateSubmitted: props.result.order.salesOrder.orderHeader.orderDate.date,
                bidCustomerNotes: props.result.order.salesOrder.orderHeader.internalNotes,
            };

            props.cartLines && props.cartLines.push(cartLine);
        }
    });
};

export const DispatchAddToCart: HandlerType = async props => {
    await addLineCollection({
        cartLineCollection: {
            cartLines: props.cartLines,
        },
        cartId: API_URL_CURRENT_FRAGMENT,
    } as AddCartLinesApiParameter);
};

export const DispatchSetShipTo: HandlerType = props => {
    const openBidShipToCustomerSequence = props.result.order.salesOrder.orderHeader.shipTo.entity.entityId;
    const shipTos = getShipTosDataView(props.getState(), {
        page: 1,
        pageSize: 20,
        expand: ["validation", "excludeShowAll", "excludeOneTime"],
    });
    if (
        openBidShipToCustomerSequence ===
        getBillToState(props.getState(), getSession(props.getState()).billToId).value?.customerNumber
    ) {
        const shipToId = shipTos.value?.find(
            shipTo => shipTo.customerNumber === openBidShipToCustomerSequence && shipTo.label === "Use Billing Address",
        )?.id;
        shipToId && setCurrentShipTo({ shipToId });
    } else {
        const shipToId = shipTos.value?.find(shipTo => shipTo.customerSequence === openBidShipToCustomerSequence)?.id;
        shipToId && setCurrentShipTo({ shipToId });
    }
};

export const LoadCart: HandlerType = async props => {
    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
    await awaitableLoadCurrentCart({ getPromotions: true })(props.dispatch, props.getState);
};

export const DispatchCompleteAcceptingBid: HandlerType = props => {
    props.dispatch({
        type: "xOpenBids/BeginLoadingOpenBids",
        isLoading: false,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.result?.errorMessage && props.parameter.onSuccess) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess();
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.result?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.result.errorMessage);
    }
};

export const chain = [
    DispatchBeginAcceptingBid,
    RequestDataFromApi,
    DispatchGetProducts,
    PrepareLineItemsInfoMap,
    PrepareCartLines,
    DispatchAddToCart,
    LoadCart,
    DispatchSetShipTo,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
    DispatchCompleteAcceptingBid,
];

const acceptBidHandler = createHandlerChainRunner(chain, "acceptBidHandler");

export default acceptBidHandler;
